import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, collection, onSnapshot, addDoc } from 'firebase/firestore';
import { Plus, Fuel, CreditCard, BarChart3, Camera, Droplets, Wrench, History, X, Check, Cloud, MapPin, Users, Table, RefreshCw, Download, ChevronLeft, ChevronRight, Settings } from 'lucide-react';

const firebaseConfig = __firebase_config;
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'troopy-tracker-final';

const CATEGORIES = [
  { id: 'fuel', label: 'Fuel', icon: '⛽', color: '#d4781a' },
  { id: 'food', label: 'Groceries', icon: '🛒', color: '#5c9e72' },
  { id: 'dining', label: 'Dining', icon: '🍽️', color: '#9e7a4a' },
  { id: 'camp', label: 'Camp', icon: '⛺', color: '#4a7a9e' },
  { id: 'maintenance', label: 'Maint.', icon: '🔧', color: '#9e4a4a' },
  { id: 'water', label: 'Water', icon: '💧', color: '#4a9e9e' },
  { id: 'activities', label: 'Activities', icon: '🏔️', color: '#7a9e4a' },
  { id: 'data', label: 'Data', icon: '📶', color: '#7a4a9e' },
  { id: 'laundry', label: 'Laundry', icon: '👕', color: '#9e9e4a' },
  { id: 'medical', label: 'Medical', icon: '💊', color: '#c44b4b' },
  { id: 'other', label: 'Other', icon: '📋', color: '#7a7560' }
];

const CURRENCIES = ['AUD', 'NZD', 'USD', 'EUR', 'GBP'];

// Helper functions
const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36);
const getMonday = (d) => { const dt = new Date(d); const day = dt.getDay(); dt.setDate(dt.getDate() - day + (day === 0 ? -6 : 1)); dt.setHours(0, 0, 0, 0); return dt; };
const fmtDate = (d) => new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtWeek = (m) => { const s = new Date(m); const e = new Date(m); e.setDate(e.getDate() + 6); return `${s.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`; };
const fmtCurrency = (n, c = 'AUD') => { try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: c, minimumFractionDigits: 2 }).format(n || 0); } catch { return '$' + (n || 0).toFixed(2); } };

// SVG Charts
const LineChart = ({ data }) => {
  if (!data || data.length < 2) return null;
  const W = 340, H = 100, pt = 8, pr = 8, pb = 26, pl = 34;
  const iW = W - pl - pr, iH = H - pt - pb;
  const vals = data.map(d => d.v);
  const mn = Math.min(...vals), mx = Math.max(...vals), rng = mx - mn || 1;
  const pts = data.map((d, i) => ({ x: pl + (i / (data.length - 1)) * iW, y: pt + iH - ((d.v - mn) / rng) * iH, lbl: d.lbl }));
  const lp = 'M' + pts.map(p => p.x.toFixed(1) + ',' + p.y.toFixed(1)).join('L');
  const ap = lp + 'L' + pts[pts.length - 1].x.toFixed(1) + ',' + (pt + iH) + 'L' + pts[0].x.toFixed(1) + ',' + (pt + iH) + 'Z';
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <defs><linearGradient id="lg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d4781a" stopOpacity="0.25"/><stop offset="100%" stopColor="#d4781a" stopOpacity="0"/></linearGradient></defs>
      {[0, 0.5, 1].map((t, i) => { const y = pt + iH - t * iH; return <g key={i}><line x1={pl} y1={y} x2={pl + iW} y2={y} stroke="#2e2c20" strokeWidth="1"/><text x={pl - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#7a7560" fontFamily="JetBrains Mono">{(mn + t * rng).toFixed(1)}</text></g>; })}
      <path d={ap} fill="url(#lg)" />
      <path d={lp} fill="none" stroke="#d4781a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => <g key={i}><circle cx={p.x} cy={p.y} r="4" fill="#d4781a" /><text x={p.x} y={H - 4} textAnchor="middle" fontSize="9" fill="#7a7560" fontFamily="JetBrains Mono">{p.lbl}</text></g>)}
    </svg>
  );
};

const BarChart = ({ data, cur }) => {
  if (!data || !data.length) return null;
  const W = 400, rowH = 34, padL = 88, padR = 80, mx = Math.max(...data.map(d => d.v), 1), h = data.length * rowH + 8;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} style={{ display: 'block' }}>
      {data.map((d, i) => { const bw = Math.max((d.v / mx) * (W - padL - padR), 4), y = i * rowH + 5; return (
        <g key={i}>
          <text x={padL - 6} y={y + 20} textAnchor="end" fontSize="12" fill="#eee8d4" fontFamily="Barlow Condensed" fontWeight="600">{d.lbl}</text>
          <rect x={padL} y={y + 4} width={bw} height="22" rx="4" fill={d.color} opacity="0.85" />
          <text x={padL + bw + 6} y={y + 20} fontSize="10" fill="#7a7560" fontFamily="JetBrains Mono">{fmtCurrency(d.v, cur)}</text>
        </g>
      ); })}
    </svg>
  );
};

// Error Boundary for graceful failure handling
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#13120e] text-[#eee8d4] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-black text-[#d4781a] mb-2">Something went wrong</h2>
            <p className="text-sm text-[#7a7560] mb-4">{this.state.error?.message || 'Unknown error'}</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} className="bg-[#d4781a] text-[#13120e] px-6 py-3 rounded-xl font-bold">
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [offlineMode, setOfflineMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tripId, setTripId] = useState(localStorage.getItem('troopy_trip_id') || 'PRIVATE-TRIP');
  const [sheetUrl, setSheetUrl] = useState(localStorage.getItem('troopy_sheet_hook') || '');
  const [expenses, setExpenses] = useState(() => {
    // Load from localStorage for offline support
    try {
      const saved = JSON.parse(localStorage.getItem('troopy_expenses') || '[]');
      return saved;
    } catch { return []; }
  });
  const [odometer, setOdometer] = useState(() => {
    try { return parseInt(localStorage.getItem('troopy_odometer') || '0'); } catch { return 0; }
  });
  const [isAdding, setIsAdding] = useState(false);
  const [isSettings, setIsSettings] = useState(false);
  const [isSyncingToSheet, setIsSyncingToSheet] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [lastBackup, setLastBackup] = useState(localStorage.getItem('troopy_last_backup') || null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [toast, setToast] = useState(null);
  const [settings, setSettings] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('troopy_settings') || '{}');
    return { vehicleName: saved.vehicleName || 'The Troopy', currency: saved.currency || 'AUD', weeklyBudget: saved.weeklyBudget || '', tankSize: saved.tankSize || '', ...saved };
  });
  const [tempSettings, setTempSettings] = useState({ ...settings, tempTripId: '', tempSheetUrl: '' });

  const toastTimeout = useRef(null);
  const showToast = useCallback((msg, color = '#5c9e72') => {
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    setToast({ msg, color });
    toastTimeout.current = setTimeout(() => setToast(null), 2800);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthLoading(true);
        setAuthError(null);
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
          console.log('Firebase: Signed in with custom token');
        } else {
          await signInAnonymously(auth);
          console.log('Firebase: Signed in anonymously');
        }
        setOfflineMode(false);
      } catch (err) {
        console.error('Auth failed:', err.code, err.message);
        setAuthError(err.code + ': ' + err.message);
        // Only retry 3 times max, then retry every 5 minutes
        if (retryCount < 3) {
          setRetryCount(c => c + 1);
          setTimeout(() => initAuth(), 2000);
        } else {
          console.log('Auth: Max retries reached, will retry in 5 min');
          setOfflineMode(true);
          // Retry every 5 minutes
          setTimeout(() => { setRetryCount(0); initAuth(); }, 300000);
        }
      } finally {
        setAuthLoading(false);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        setAuthError(null);
        console.log('Firebase: User authenticated');
      }
    });
    return () => unsubscribe();
  }, [showToast, retryCount]);

  useEffect(() => {
    localStorage.setItem('troopy_settings', JSON.stringify(settings));
  }, [settings]);

  // Persist expenses to localStorage for offline support
  useEffect(() => {
    localStorage.setItem('troopy_expenses', JSON.stringify(expenses));
    localStorage.setItem('troopy_odometer', odometer.toString());
  }, [expenses, odometer]);

  // Auto-sync pending entries when auth connects
  useEffect(() => {
    if (!user || !tripId) return;
    
    const syncPending = async () => {
      const pending = expenses.filter(e => e._pendingSync && !e.synced);
      if (pending.length === 0) return;
      
      console.log(`Syncing ${pending.length} pending entries...`);
      for (const entry of pending) {
        try {
          await addDoc(collection(db, 'artifacts', appId, 'public', 'data', tripId), entry);
          if (entry.odomEnd) {
            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', tripId, 'vehicle'), { type: 'vehicle', odometer: entry.odomEnd }, { merge: true });
          }
          await syncToSheet(entry);
          setExpenses(p => p.map(e => e.id === entry.id ? { ...e, synced: true, _pendingSync: false } : e));
        } catch (err) {
          console.error('Sync failed for entry:', entry.id, err);
        }
      }
      if (pending.length > 0) {
        showToast(`Synced ${pending.length} offline entries ✓`);
      }
    };
    
    syncPending();
  }, [user, tripId]); // Sync when auth connects

  useEffect(() => {
    if (!user || !tripId) return;
    const tripCol = collection(db, 'artifacts', appId, 'public', 'data', tripId);
    const unsubscribe = onSnapshot(tripCol, (snapshot) => {
      const remoteData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const remoteExpenses = remoteData.filter(d => d.type === 'expense').map(e => ({ ...e, synced: true, _pendingSync: false }));
      const vehicleData = remoteData.find(d => d.type === 'vehicle');
      
      // Merge: keep local pending entries, deduplicate by ID
      setExpenses(prev => {
        const remoteIds = new Set(remoteExpenses.map(e => e.id));
        const localPending = prev.filter(e => e._pendingSync && !remoteIds.has(e.id));
        const merged = [...remoteExpenses, ...localPending].sort((a, b) => new Date(b.date) - new Date(a.date));
        return merged;
      });
      
      if (vehicleData && vehicleData.odometer > odometer) {
        setOdometer(vehicleData.odometer);
      }
    }, (err) => {
      console.error('Firestore listener error:', err);
    });
    return () => unsubscribe();
  }, [user, tripId]);

  const thisMonday = useMemo(() => { const d = getMonday(new Date()); d.setDate(d.getDate() + weekOffset * 7); return d; }, [weekOffset]);
  const weekEnd = useMemo(() => { const d = new Date(thisMonday); d.setDate(d.getDate() + 7); return d; }, [thisMonday]);

  const curWeekEntries = useMemo(() => expenses.filter(e => { const d = new Date(e.date); return d >= thisMonday && d < weekEnd; }), [expenses, thisMonday, weekEnd]);
  const curWeekTotal = useMemo(() => curWeekEntries.reduce((s, e) => s + (e.amount || 0), 0), [curWeekEntries]);
  const budgetPct = settings.weeklyBudget ? Math.min((curWeekTotal / parseFloat(settings.weeklyBudget)) * 100, 100) : 0;
  const budgetColor = budgetPct > 85 ? '#b84040' : budgetPct > 65 ? '#c9a844' : '#5c9e72';

  const fuelFills = useMemo(() => expenses.filter(e => e.category === 'fuel' && e.litres && e.odomStart && e.odomEnd).sort((a, b) => new Date(a.date) - new Date(b.date)), [expenses]);
  const lastL100 = useMemo(() => { if (!fuelFills.length) return null; const f = fuelFills[fuelFills.length - 1]; return ((f.litres / (f.odomEnd - f.odomStart)) * 100).toFixed(1); }, [fuelFills]);
  const avgL100 = useMemo(() => { if (fuelFills.length < 2) return null; const total = fuelFills.reduce((a, f) => ({ l: a.l + f.litres, km: a.km + (f.odomEnd - f.odomStart) }), { l: 0, km: 0 }); return total.km > 0 ? ((total.l / total.km) * 100).toFixed(1) : null; }, [fuelFills]);
  const tripKm = useMemo(() => fuelFills.length ? (fuelFills[fuelFills.length - 1].odomEnd - fuelFills[0].odomStart) : 0, [fuelFills]);
  const fuelSpent = useMemo(() => expenses.filter(e => e.category === 'fuel').reduce((s, e) => s + (e.amount || 0), 0), [expenses]);
  const fuelChartData = useMemo(() => fuelFills.slice(-8).map(f => ({ lbl: new Date(f.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }), v: parseFloat(((f.litres / (f.odomEnd - f.odomStart)) * 100).toFixed(2)) })), [fuelFills]);
  const catChartData = useMemo(() => { const m = {}; curWeekEntries.forEach(e => { m[e.category] = (m[e.category] || 0) + (e.amount || 0); }); return Object.entries(m).map(([c, v]) => { const cat = CATEGORIES.find(x => x.id === c) || CATEGORIES[CATEGORIES.length - 1]; return { lbl: cat.label, v, color: cat.color }; }).sort((a, b) => b.v - a.v); }, [curWeekEntries]);

  const [formData, setFormData] = useState({ amount: '', category: 'fuel', litres: '', odomStart: '', odomEnd: '', pricePerLitre: '', note: '', location: '', date: new Date().toISOString().split('T')[0] });
  const autoAmount = useMemo(() => { const l = parseFloat(formData.litres), p = parseFloat(formData.pricePerLitre); return (l > 0 && p > 0) ? (l * p).toFixed(2) : ''; }, [formData.litres, formData.pricePerLitre]);
  const calcL100 = useMemo(() => { const l = parseFloat(formData.litres), s = parseFloat(formData.odomStart), e = parseFloat(formData.odomEnd); return (l > 0 && e > s) ? ((l / (e - s)) * 100).toFixed(1) : null; }, [formData.litres, formData.odomStart, formData.odomEnd]);

  const syncToSheet = async (data) => { if (!sheetUrl) return; try { await fetch(sheetUrl, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); } catch (e) { console.error('Sheet sync failed', e); } };
  const exportToCSV = () => { setIsExporting(true); const headers = ['Date', 'Category', 'Amount', 'Litres', 'OdomStart', 'OdomEnd', 'Note']; const rows = expenses.map(exp => [exp.date, CATEGORIES.find(c => c.id === exp.category)?.label || exp.category, exp.amount, exp.litres || '', exp.odomStart || '', exp.odomEnd || '', exp.note || '']); const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n'); const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `troopy-expenses-${tripId}-${new Date().toISOString().split('T')[0]}.csv`; a.click(); URL.revokeObjectURL(url); setIsExporting(false); };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      amount: entry.amount?.toString() || '',
      category: entry.category || 'fuel',
      litres: entry.litres?.toString() || '',
      odomStart: entry.odomStart?.toString() || '',
      odomEnd: entry.odomEnd?.toString() || '',
      pricePerLitre: entry.pricePerLitre?.toString() || '',
      note: entry.note || '',
      location: entry.location || '',
      date: entry.date || new Date().toISOString().split('T')[0]
    });
    setIsAdding(true);
  };

  const handleDeleteEntry = async (entry) => {
    if (!confirm('Delete this entry?')) return;
    setExpenses(p => p.filter(e => e.id !== entry.id));
    if (user && tripId) {
      try {
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', tripId, entry.id), { _deleted: true }, { merge: true });
      } catch (err) {
        console.error('Delete sync failed:', err);
      }
    }
    showToast('Entry deleted', '#b84040');
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    
    // Input validation
    const amountVal = parseFloat(formData.amount || autoAmount);
    if (!amountVal || amountVal <= 0) { 
      showToast('Enter an amount', '#b84040'); 
      return; 
    }
    
    // Validate odometer for fuel entries
    if (formData.category === 'fuel') {
      const odomS = parseInt(formData.odomStart);
      const odomE = parseInt(formData.odomEnd);
      if (odomS && odomE && odomE <= odomS) {
        showToast('Odom end must be greater than start', '#b84040');
        return;
      }
      if (odomS && odometer && odomS < odometer) {
        showToast('Odom start below current odometer', '#c9a844');
        // Allow but warn
      }
    }
    
    // Validate date
    const entryDate = new Date(formData.date);
    if (isNaN(entryDate.getTime())) {
      showToast('Invalid date', '#b84040');
      return;
    }
    
    // Check if editing or creating
    if (editingEntry) {
      // Update existing entry
      const updatedEntry = {
        ...editingEntry,
        ...formData,
        amount: amountVal,
        litres: formData.category === 'fuel' ? parseFloat(formData.litres) || null : null,
        pricePerLitre: formData.category === 'fuel' ? parseFloat(formData.pricePerLitre) || null : null,
        odomStart: formData.category === 'fuel' ? parseInt(formData.odomStart) || null : null,
        odomEnd: formData.category === 'fuel' ? parseInt(formData.odomEnd) || null : null,
        synced: false,
        _pendingSync: !user,
        updatedAt: new Date().toISOString()
      };
      
      setExpenses(p => p.map(e => e.id === editingEntry.id ? updatedEntry : e));
      setEditingEntry(null);
      setIsAdding(false);
      setFormData({ amount: '', category: 'fuel', litres: '', odomStart: '', odomEnd: '', pricePerLitre: '', note: '', location: '', date: new Date().toISOString().split('T')[0] });
      
      // Try to sync update
      if (user && tripId) {
        try {
          await setDoc(doc(db, 'artifacts', appId, 'public', 'data', tripId, editingEntry.id), updatedEntry, { merge: true });
          await syncToSheet(updatedEntry);
          setExpenses(p => p.map(e => e.id === editingEntry.id ? { ...e, synced: true, _pendingSync: false } : e));
          showToast('Entry updated ✓');
        } catch (err) {
          showToast('Updated locally - will sync later', '#c9a844');
        }
      } else {
        showToast('Updated locally ✓');
      }
      return;
    }
    
    // Create new entry
    const newEntry = { 
      type: 'expense', 
      id: uid(), 
      ...formData, 
      amount: amountVal,
      litres: formData.category === 'fuel' ? parseFloat(formData.litres) || null : null, 
      pricePerLitre: formData.category === 'fuel' ? parseFloat(formData.pricePerLitre) || null : null, 
      odomStart: formData.category === 'fuel' ? parseInt(formData.odomStart) || null : null, 
      odomEnd: formData.category === 'fuel' ? parseInt(formData.odomEnd) || null : null, 
      synced: false, 
      createdAt: new Date().toISOString(),
      _pendingSync: !user
    };
    
    // Always save to local state immediately (offline-first)
    setExpenses(p => [newEntry, ...p]);
    setIsAdding(false);
    setFormData({ amount: '', category: 'fuel', litres: '', odomStart: formData.odomEnd || '', odomEnd: '', pricePerLitre: '', note: '', location: '', date: new Date().toISOString().split('T')[0] });
    
    // Try to sync to Firebase if online
    if (user && tripId) {
      try {
        console.log('Syncing to Firestore...', newEntry);
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', tripId), newEntry);
        
        if (newEntry.odomEnd && newEntry.odomEnd > odometer) { 
          await setDoc(doc(db, 'artifacts', appId, 'public', 'data', tripId, 'vehicle'), { type: 'vehicle', odometer: newEntry.odomEnd }, { merge: true }); 
        }
        
        await syncToSheet(newEntry);
        
        // Mark as synced
        setExpenses(p => p.map(e => e.id === newEntry.id ? { ...e, synced: true, _pendingSync: false } : e));
        showToast('Entry saved & synced ✓');
      } catch (err) { 
        console.error('Sync failed:', err);
        showToast('Saved locally - will sync when online', '#c9a844'); 
      }
    } else {
      showToast('Saved locally - will sync when online', '#c9a844');
    }
  };

  const saveSettings = () => {
    if (tempSettings.tempTripId) { const cleanId = tempSettings.tempTripId.trim().toUpperCase().replace(/\s+/g, '-'); setTripId(cleanId); localStorage.setItem('troopy_trip_id', cleanId); }
    if (tempSettings.tempSheetUrl) { setSheetUrl(tempSettings.tempSheetUrl); localStorage.setItem('troopy_sheet_hook', tempSettings.tempSheetUrl); }
    setSettings({ vehicleName: tempSettings.vehicleName, currency: tempSettings.currency, weeklyBudget: tempSettings.weeklyBudget, tankSize: tempSettings.tankSize });
    setIsSettings(false);
    showToast('Settings saved ✓');
  };

  const forceFullSync = async () => { if (!sheetUrl) return; setIsSyncingToSheet(true); for (const exp of expenses) { if (!exp.synced) await syncToSheet(exp); } setIsSyncingToSheet(false); showToast('Full sync complete ✓'); };

  const totalSpent = useMemo(() => expenses.reduce((sum, e) => sum + (e.amount || 0), 0), [expenses]);
  const unsyncedCount = useMemo(() => expenses.filter(e => !e.synced).length, [expenses]);

  return (
    <div className="min-h-screen bg-[#13120e] text-[#eee8d4] font-sans p-4 pb-28">
      <div className="max-w-xl mx-auto flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-black italic tracking-tighter text-[#d4781a]">TROOPY TRACKER</h1>
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#7a7560] uppercase"><span>🚗</span><span>{settings.vehicleName} · {tripId}</span>{offlineMode && <button onClick={() => { setRetryCount(0); setOfflineMode(false); setAuthLoading(true); signInAnonymously(auth).then(() => { setOfflineMode(false); showToast('Connected! Syncing...'); }).catch((err) => { setOfflineMode(true); setAuthError(err.code + ': ' + err.message); showToast('Auth error: ' + (err.code || 'unknown'), '#b84040'); }).finally(() => setAuthLoading(false)); }} className="ml-1 px-2 py-0.5 rounded bg-[#c9a844]/20 text-[#c9a844] hover:bg-[#c9a844]/30 transition-colors">RETRY</button>}</div>
          {authError && <div className="text-[9px] text-[#b84040] mt-1 font-mono truncate max-w-[300px]">{authError}</div>}
        </div>
        <div className="bg-[#1c1b14] border border-[#2e2c20] px-4 py-2 rounded-2xl text-right">
          <p className="text-[10px] text-[#7a7560] uppercase font-bold">Odo</p>
          <p className="font-mono text-xl text-[#d4781a]">{odometer.toLocaleString()} <span className="text-xs">km</span></p>
        </div>
      </div>

      <div className="max-w-xl mx-auto space-y-5">
        {activeTab === 'dashboard' && (
          <>
            <div className="bg-[#232218] border border-[#d4781a] rounded-xl p-4">
              <div className="text-[10px] text-[#7a7560] font-bold uppercase mb-1">WEEK · {fmtWeek(thisMonday)}</div>
              <div className="text-[40px] font-black text-[#d4781a] leading-none">{fmtCurrency(curWeekTotal, settings.currency)}</div>
              {settings.weeklyBudget && (
                <>
                  <div className="flex justify-between text-[11px] font-mono text-[#7a7560] mt-3"><span>WEEKLY BUDGET</span><span style={{ color: budgetColor }}>{budgetPct.toFixed(0)}% of {fmtCurrency(parseFloat(settings.weeklyBudget), settings.currency)}</span></div>
                  <div className="bg-[#1c1b14] rounded h-2 mt-2 overflow-hidden"><div className="h-full rounded" style={{ width: budgetPct + '%', background: budgetColor }} /></div>
                </>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#232218] border border-[#2e2c20] p-4 rounded-xl text-center"><div className="text-[10px] text-[#7a7560] font-bold uppercase mb-1">L/100KM</div><div className="text-2xl font-black text-[#c9a844]">{lastL100 || '—'}</div>{avgL100 && <div className="text-[10px] text-[#7a7560] mt-1">avg {avgL100}</div>}</div>
              <div className="bg-[#232218] border border-[#2e2c20] p-4 rounded-xl text-center"><div className="text-[10px] text-[#7a7560] font-bold uppercase mb-1">TRIP KM</div><div className="text-2xl font-black">{tripKm > 0 ? tripKm.toLocaleString() : '—'}</div></div>
              <div className="bg-[#232218] border border-[#2e2c20] p-4 rounded-xl text-center"><div className="text-[10px] text-[#7a7560] font-bold uppercase mb-1">FUEL $</div><div className="text-xl font-black text-[#d4781a]">{fuelSpent > 0 ? fmtCurrency(fuelSpent, settings.currency).replace(/\.00$/, '') : '—'}</div></div>
            </div>

            {fuelChartData.length > 1 && (
              <div className="bg-[#232218] border border-[#2e2c20] p-4 rounded-xl"><div className="text-[10px] text-[#7a7560] font-bold uppercase mb-3">Fuel Efficiency — L/100km</div><LineChart data={fuelChartData} /></div>
            )}
            {catChartData.length > 0 && (
              <div className="bg-[#232218] border border-[#2e2c20] p-4 rounded-xl"><div className="text-[10px] text-[#7a7560] font-bold uppercase mb-3">This Week by Category</div><BarChart data={catChartData} cur={settings.currency} /></div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setIsAdding(true)} className="flex-1 bg-[#d4781a] text-[#13120e] p-5 rounded-xl font-black text-lg active:scale-95 transition-all">LOG EXPENSE</button>
              <button onClick={() => setIsSettings(true)} className="w-20 bg-[#1c1b14] border border-[#2e2c20] p-5 rounded-xl flex items-center justify-center"><Settings className="w-6 h-6" /></button>
            </div>

            <div className="space-y-2">
              <h3 className="text-[10px] font-bold text-[#7a7560] uppercase tracking-widest">Recent Entries</h3>
              {expenses.slice(0, 10).map(exp => { const cat = CATEGORIES.find(c => c.id === exp.category) || CATEGORIES[CATEGORIES.length - 1]; const dist = (exp.odomEnd && exp.odomStart) ? exp.odomEnd - exp.odomStart : null; const l100 = (dist && exp.litres) ? ((exp.litres / dist) * 100).toFixed(1) : null; return (
                <div key={exp.id} className="bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl flex items-center justify-between group">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-xl flex-shrink-0">{cat.icon}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-sm truncate">{exp.desc || cat.label}</p>
                      <p className="text-[9px] text-[#7a7560]">{fmtDate(exp.date)}{exp.location && ` · ${exp.location}`}{exp.litres && ` · ${exp.litres}L`}{l100 && ` · ${l100}L/100`}{exp._pendingSync && ' · ⏳'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right"><p className="font-black text-[#d4781a]">{fmtCurrency(exp.amount, settings.currency)}</p></div>
                    <button onClick={() => handleEditEntry(exp)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-[#2e2c20] transition-all" title="Edit"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7a7560" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                  </div>
                </div>
              ); })}
            </div>
          </>
        )}

        {activeTab === 'logbook' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 bg-[#1c1b14] border border-[#2e2c20] rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
              <div className="text-center">
                <div className="text-[10px] text-[#7a7560] font-mono">{fmtWeek(thisMonday)}</div>
                <div className="text-lg font-black text-[#d4781a]">{fmtCurrency(curWeekTotal, settings.currency)}</div>
              </div>
              <button onClick={() => setWeekOffset(w => Math.min(w + 1, 0))} disabled={weekOffset >= 0} className="p-2 bg-[#1c1b14] border border-[#2e2c20] rounded-lg disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
            </div>
            {curWeekEntries.length === 0 ? (
              <div className="text-center py-12 text-[#7a7560]"><div className="text-4xl mb-2">📖</div><div>No entries this week</div></div>
            ) : (
              <div className="space-y-4">
                {[...new Set(curWeekEntries.map(e => e.date))].sort().reverse().map(day => {
                  const dayEntries = curWeekEntries.filter(e => e.date === day);
                  const dayTotal = dayEntries.reduce((s, e) => s + (e.amount || 0), 0);
                  return (
                    <div key={day}>
                      <div className="flex justify-between items-center mb-2"><span className="text-[10px] text-[#7a7560] font-bold uppercase">{fmtDate(day)}</span><span className="text-sm font-mono">{fmtCurrency(dayTotal, settings.currency)}</span></div>
                      {dayEntries.map(exp => { const cat = CATEGORIES.find(c => c.id === exp.category) || CATEGORIES[CATEGORIES.length - 1]; return (
                        <div key={exp.id} className="bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl mb-2 flex items-center justify-between group">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-xl flex-shrink-0">{cat.icon}</span>
                            <div className="min-w-0"><p className="font-bold text-sm truncate">{exp.desc || cat.label}</p><p className="text-[9px] text-[#7a7560]">{exp.location || cat.label}{exp._pendingSync && ' · ⏳'}</p></div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-right"><p className="font-black text-[#d4781a]">{fmtCurrency(exp.amount, settings.currency)}</p></div>
                            <button onClick={() => handleEditEntry(exp)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-[#2e2c20] transition-all" title="Edit"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7a7560" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                          </div>
                        </div>
                      ); })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {isAdding && (
        <div className="fixed inset-0 bg-black/95 flex items-end md:items-center justify-center p-4 z-50">
          <div className="bg-[#232218] w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 border border-[#2e2c20]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black italic">{editingEntry ? 'EDIT LOG' : 'NEW LOG'}</h2>
              <button onClick={() => { setIsAdding(false); setEditingEntry(null); }} className="p-2 bg-[#1c1b14] rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSaveExpense} className="space-y-4">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {CATEGORIES.map(cat => (
                  <button key={cat.id} type="button" onClick={() => setFormData({ ...formData, category: cat.id })} className={`flex-shrink-0 px-3 py-2 rounded-xl border text-sm font-bold ${formData.category === cat.id ? 'border-[#d4781a] bg-[#d4781a]/10 text-[#d4781a]' : 'border-[#2e2c20] text-[#7a7560]'}`}>
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Date</label>
                <input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" />
              </div>
              {formData.category === 'fuel' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] text-[#7a7560] uppercase font-bold">Odom Start (km)</label><input type="number" value={formData.odomStart} onChange={e => setFormData({ ...formData, odomStart: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder={odometer || '124500'} /></div>
                    <div><label className="text-[10px] text-[#7a7560] uppercase font-bold">Odom End (km)</label><input type="number" value={formData.odomEnd} onChange={e => setFormData({ ...formData, odomEnd: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="124890" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] text-[#7a7560] uppercase font-bold">Litres</label><input type="number" step="0.1" value={formData.litres} onChange={e => setFormData({ ...formData, litres: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="72.4" /></div>
                    <div><label className="text-[10px] text-[#7a7560] uppercase font-bold">Price/L ($)</label><input type="number" step="0.001" value={formData.pricePerLitre} onChange={e => setFormData({ ...formData, pricePerLitre: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="2.18" /></div>
                  </div>
                  {(calcL100 || autoAmount) && (
                    <div className="bg-[#1c1b14] border border-[#d4781a] rounded-xl p-4 flex justify-between">
                      {calcL100 && <div><div className="text-[10px] text-[#7a7560]">L/100KM</div><div className="text-3xl font-black text-[#d4781a]">{calcL100}</div></div>}
                      {autoAmount && <div><div className="text-[10px] text-[#7a7560]">Auto Total</div><div className="text-2xl font-black text-[#c9a844]">${autoAmount}</div></div>}
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Amount ($)</label>
                <input type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder={autoAmount || '0.00'} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-2xl font-black text-white" />
              </div>
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Location / Note</label>
                <input type="text" value={formData.note} onChange={e => setFormData({ ...formData, note: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="e.g. BP Alice Springs" />
              </div>
              <button type="submit" className="w-full bg-[#d4781a] text-[#13120e] p-4 rounded-xl font-black text-lg">SAVE ENTRY</button>
            </form>
          </div>
        </div>
      )}

      {isSettings && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#232218] w-full max-w-md rounded-3xl p-6 border border-[#2e2c20] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black italic">SETTINGS</h2>
              <button onClick={() => setIsSettings(false)} className="p-2 bg-[#1c1b14] rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-6">
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Vehicle Name</label>
                <input type="text" value={tempSettings.vehicleName} onChange={e => setTempSettings({ ...tempSettings, vehicleName: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#7a7560] uppercase font-bold">Weekly Budget ($)</label>
                  <input type="number" value={tempSettings.weeklyBudget} onChange={e => setTempSettings({ ...tempSettings, weeklyBudget: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="600" />
                </div>
                <div>
                  <label className="text-[10px] text-[#7a7560] uppercase font-bold">Tank Size (L)</label>
                  <input type="number" value={tempSettings.tankSize} onChange={e => setTempSettings({ ...tempSettings, tankSize: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white" placeholder="180" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Currency</label>
                <select value={tempSettings.currency} onChange={e => setTempSettings({ ...tempSettings, currency: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[#7a7560] uppercase font-bold">Trip ID</label>
                <input type="text" value={tempSettings.tempTripId || tripId} onChange={e => setTempSettings({ ...tempSettings, tempTripId: e.target.value })} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl text-white font-mono text-sm" />
              </div>
              <div className="bg-[#1c1b14] border border-[#2e2c20] rounded-xl p-4">
                <div className="text-[10px] text-[#7a7560] uppercase font-bold mb-2">Google Sheets Sync</div>
                <p className="text-xs text-[#7a7560] mb-3">
                  1. Create Sheet → Extensions → Apps Script<br/>
                  2. Paste code from GOOGLE_SHEETS_SETUP.md<br/>
                  3. Deploy → Web App → Copy URL<br/>
                  4. Paste URL below
                </p>
                <input type="text" value={tempSettings.tempSheetUrl || sheetUrl} onChange={e => setTempSettings({ ...tempSettings, tempSheetUrl: e.target.value })} placeholder="https://script.google.com/macros/s/.../exec" className="w-full bg-[#13120e] border border-[#2e2c20] p-3 rounded-xl text-white text-xs font-mono mb-2" />
                {sheetUrl && <div className="text-[10px] text-green-500 mb-2">✓ Sheets configured</div>}
              </div>
              <div className="bg-[#1c1b14] border border-[#2e2c20] rounded-xl p-4">
                <div className="text-[10px] text-[#7a7560] uppercase font-bold mb-2">Trip Summary</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Entries: <span className="text-[#d4781a]">{expenses.length}</span></div>
                  <div>Total: <span className="text-[#d4781a]">{fmtCurrency(totalSpent, settings.currency)}</span></div>
                  <div>Fuel: <span className="text-[#d4781a]">{fmtCurrency(fuelSpent, settings.currency)}</span></div>
                  <div>Fills: <span className="text-[#d4781a]">{fuelFills.length}</span></div>
                  <div>km: <span className="text-[#d4781a]">{tripKm > 0 ? tripKm.toLocaleString() : '—'}</span></div>
                  <div>Avg: <span className="text-[#d4781a]">{avgL100 ? avgL100 + ' L/100' : '—'}</span></div>
                </div>
              </div>
              <button onClick={exportToCSV} disabled={isExporting} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl flex items-center justify-center gap-2 text-sm font-bold">
                {isExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
              </button>
              <button onClick={forceFullSync} disabled={isSyncingToSheet || !sheetUrl} className="w-full bg-[#1c1b14] border border-[#2e2c20] p-3 rounded-xl flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50">
                {isSyncingToSheet ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />} Sync to Sheets ({unsyncedCount})
              </button>
              <button onClick={saveSettings} className="w-full bg-[#d4781a] text-[#13120e] p-4 rounded-xl font-black text-lg">SAVE SETTINGS</button>
            </div>
          </div>
        </div>
      )}

      <nav className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1c1b14]/90 backdrop-blur border border-[#2e2c20] p-2 rounded-full flex gap-2 z-40">
        <button onClick={() => setActiveTab('dashboard')} className={`p-4 rounded-full transition-all ${activeTab === 'dashboard' ? 'bg-[#d4781a] text-[#13120e]' : 'text-[#7a7560]'}`}><BarChart3 className="w-6 h-6" /></button>
        <button onClick={() => setActiveTab('logbook')} className={`p-4 rounded-full transition-all ${activeTab === 'logbook' ? 'bg-[#d4781a] text-[#13120e]' : 'text-[#7a7560]'}`}><History className="w-6 h-6" /></button>
        <button onClick={() => setIsAdding(true)} className="p-4 rounded-full bg-[#d4781a] text-[#13120e]"><Plus className="w-6 h-6" /></button>
      </nav>

      {toast && <div className="fixed top-20 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl font-bold text-sm z-[100]" style={{ background: toast.color, color: toast.color === '#b84040' ? '#fff' : '#13120e' }}>{toast.msg}</div>}
    </div>
  );
};

export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}